import { useContext, useEffect, useState, useCallback } from "react";
import { WebsocketProviderContext } from "@/components/WebsocketProvider";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";

export interface DiscordMessage {
    id: string;
    channelId: string;
    serverId: string;
    author: {
        id: string;
        username: string;
        discriminator?: string;
        avatar?: string;
        bot?: boolean;
    };
    content: string;
    timestamp: string;
    attachments?: Array<{
        id: string;
        url: string;
        name: string;
    }>;
    embeds?: any[];
    stickers?: Array<{
        id: string;
        name: string;
        description?: string;
        tags?: string;
        type: number;
        format: number;
        url: string;
    }>;
    edited?: boolean;
    editedTimestamp?: string;
}

interface UseDiscordChatOptions {
    channelIds: string[];
    serverId: string;
}

export const useDiscordChat = ({ channelIds, serverId }: UseDiscordChatOptions) => {
    const { socket, socketConnection } = useContext(WebsocketProviderContext);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);

    const [messages, setMessages] = useState<Map<string, DiscordMessage[]>>(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [canViewChat, setCanViewChat] = useState(false);
    const [canSendMessages, setCanSendMessages] = useState(false);

    // Check if user has Discord linked (required for both viewing and sending)
    useEffect(() => {
        const hasDiscordLinked = !!currentUser?.user?.discord?.id;
        setCanViewChat(hasDiscordLinked);
        setCanSendMessages(hasDiscordLinked);

        if (!hasDiscordLinked) {
            setError("Discord account not linked");
            setIsLoading(false);
        } else {
            setError(null);
        }
    }, [currentUser]);

    // Initialize channel messages map
    useEffect(() => {
        const initialMap = new Map<string, DiscordMessage[]>();
        channelIds.forEach(channelId => {
            initialMap.set(channelId, []);
        });
        setMessages(initialMap);
    }, [channelIds]);

    // Socket event handlers
    useEffect(() => {
        if (!socket || !socketConnection) {
            setIsConnected(false);
            return;
        }

        if (!canViewChat) {
            setIsConnected(false);
            return;
        }

        setIsConnected(true);

        // Listen for initial messages load
        const handleInitialMessages = (data: { channelId: string; messages: DiscordMessage[] }) => {
            setMessages(prev => {
                const newMap = new Map(prev);
                newMap.set(data.channelId, data.messages);
                return newMap;
            });
            setIsLoading(false);
        };

        // Listen for new messages (cache-first architecture)
        const handleNewMessage = (data: { channelId: string; message: DiscordMessage }) => {
            const { channelId, message } = data;
            // Only add if it's from one of our channels
            if (channelIds.includes(channelId)) {
                setMessages(prev => {
                    const newMap = new Map(prev);
                    const channelMessages = newMap.get(channelId) || [];

                    // Check if message already exists to prevent duplicates
                    const existingMessage = channelMessages.find(msg => msg.id === message.id);
                    if (!existingMessage) {
                        newMap.set(channelId, [...channelMessages, message]);
                    }
                    return newMap;
                });
            }
        };

        // Listen for message updates (cache-first architecture)
        const handleMessageUpdate = (data: { channelId: string; message: DiscordMessage }) => {
            const { channelId, message } = data;
            setMessages(prev => {
                const newMap = new Map(prev);
                const channelMessages = newMap.get(channelId) || [];
                const updatedMessages = channelMessages.map(msg =>
                    msg.id === message.id ? message : msg);
                newMap.set(channelId, updatedMessages);
                return newMap;
            });
        };

        // Listen for message deletions (cache-first architecture)
        const handleMessageDelete = (data: { messageId: string; channelId: string }) => {
            setMessages(prev => {
                const newMap = new Map(prev);
                const channelMessages = newMap.get(data.channelId) || [];
                const filteredMessages = channelMessages.filter(msg => msg.id !== data.messageId);
                newMap.set(data.channelId, filteredMessages);
                return newMap;
            });
        };

        // Listen for errors
        const handleError = (error: { message: string }) => {
            setError(error.message);
            console.error("Discord chat error:", error);
        };

        // Subscribe to Discord events
        socket.on("DISCORD_INITIAL_MESSAGES", handleInitialMessages);
        socket.on("DISCORD_MESSAGE_CREATE", handleNewMessage);
        socket.on("DISCORD_MESSAGE_UPDATE", handleMessageUpdate);
        socket.on("DISCORD_MESSAGE_DELETE", handleMessageDelete);
        socket.on("discord:error", handleError);

        // Request messages for each channel (no subscription needed)
        channelIds.forEach(channelId => {
            socket.emit("discord:requestMessages", {
                channelId,
                serverId,
            });
        });

        return () => {
            // Unsubscribe from events only (no need to leave channels)
            if (socket) {
                socket.off("DISCORD_INITIAL_MESSAGES", handleInitialMessages);
                socket.off("DISCORD_MESSAGE_CREATE", handleNewMessage);
                socket.off("DISCORD_MESSAGE_UPDATE", handleMessageUpdate);
                socket.off("DISCORD_MESSAGE_DELETE", handleMessageDelete);
                socket.off("discord:error", handleError);
            }
        };
    }, [socket, socketConnection, channelIds, serverId, currentUser, canViewChat]);

    // Send message function
    const sendMessage = useCallback(async (channelId: string, content: string) => {
        if (!socket || !canSendMessages || !content.trim()) {
            return false;
        }

        return new Promise<boolean>((resolve) => {
            socket.emit("discord:sendMessage", {
                channelId,
                serverId,
                content: content.trim(),
            }, (response: { success: boolean; error?: string }) => {
                if (response.error) {
                    console.error("[useDiscordChat] Send message error:", response.error);
                    setError(response.error);
                    resolve(false);
                } else {
                    resolve(true);
                }
            });
        });
    }, [socket, canSendMessages, serverId]);

    // Get messages for a specific channel
    const getChannelMessages = useCallback((channelId: string): DiscordMessage[] => {
        return messages.get(channelId) || [];
    }, [messages]);

    return {
        messages,
        getChannelMessages,
        sendMessage,
        isLoading,
        error,
        isConnected,
        canViewChat,
        canSendMessages,
        socketConnection,
    };
};